import { ApiClientBase } from '@@/api/api';
import { baseApiResponse, RequestBase } from '@@/api/api-common';
import { ApiResult } from '@@/types/ApiResult';
import { CreateItemPayload, UpdateItemPayload, ListItemsRequest, GetItemRequest } from '../schemas/item';

export interface ItemRecord {
  entityId: string;
  attributes: Record<string, unknown>;
  itemId: string;
  primaryKey: string;
  lastUpdatedBy: string;
  timestamp: string;
}

export interface ListItemsResponse extends baseApiResponse {
  items: ItemRecord[];
}

export interface GetItemResponse extends baseApiResponse {
  item: ItemRecord;
}

export interface CreateItemResponse extends baseApiResponse {
  item: ItemRecord;
}

export interface UpdateItemResponse extends baseApiResponse {
  item: {
    path: string;
    entity: string;
  };
}

export interface DeleteItemRequest {
  entityId: string;
  itemId: string;
}

export interface ItemReference {
  referenceId: string;
  sourceEntityId: string;
  sourceItemId: string;
  sourceField: string;
  sourcePrimaryKey: string;
  targetEntityId: string;
  targetItemId: string;
  targetPrimaryKey: string;
}

export interface GetItemReferencesResponse extends baseApiResponse {
  referencedBy: ItemReference[];
  referencesTo: ItemReference[];
}

export class ItemsClient extends ApiClientBase {
  async listItems({ input, queryString }: RequestBase<ListItemsRequest>): Promise<ApiResult<ListItemsResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString);

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${input.entityId}/items?${queryParams}`), {
        method: 'GET',
        headers
      });

      if (!result.ok) {
        return this.error('Failed to get items');
      }

      return await result.json();
    } catch {
      return this.error('Failed to get items');
    }
  }

  async getItem({ input, queryString }: RequestBase<GetItemRequest>): Promise<ApiResult<GetItemResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString || {});

      const result = await fetch(
        this.getDfcApiUrl(`/dfc/entity/${input.entityId}/items/${input.itemId}?${queryParams}`),
        {
          method: 'GET',
          headers
        }
      );

      if (!result.ok) {
        return this.error('Failed to get item');
      }

      return await result.json();
    } catch {
      return this.error('Failed to get item');
    }
  }

  async createItem({
    entityId,
    input,
    queryString
  }: {
    entityId: string;
    input: CreateItemPayload;
    queryString?: Record<string, unknown>;
  }): Promise<ApiResult<CreateItemResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString || {});

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${entityId}/items?${queryParams}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.data.message);
      }

      return await result.json();
    } catch {
      return this.error('Unknown');
    }
  }

  async updateItem({
    entity,
    path,
    input,
    queryString
  }: {
    entity: string;
    path: string;
    input: UpdateItemPayload;
    queryString?: Record<string, unknown>;
  }): Promise<ApiResult<UpdateItemResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString || {});

      const result = await fetch(this.getDfcApiUrl(`/dfc/entity/${entity}/items/${path}?${queryParams}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify(input)
      });

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.data.message);
      }

      return await result.json();
    } catch {
      return this.error('Unknown');
    }
  }

  async deleteItem({ input, queryString }: RequestBase<DeleteItemRequest>): Promise<ApiResult<baseApiResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString || {});

      const result = await fetch(
        this.getDfcApiUrl(`/dfc/entity/${input.entityId}/items/${input.itemId}?${queryParams}`),
        {
          method: 'DELETE',
          headers
        }
      );

      if (!result.ok) {
        const jsonResult = await result.json();
        return this.error(jsonResult.data.message);
      }

      return await result.json();
    } catch {
      return this.error('Failed to delete item');
    }
  }

  async getItemReferences({
    input,
    queryString
  }: RequestBase<GetItemRequest>): Promise<ApiResult<GetItemReferencesResponse>> {
    try {
      const headers = await this.getHeaders();
      const queryParams = this.formatQueryStrings(queryString || {});

      const result = await fetch(
        this.getDfcApiUrl(`/dfc/entity/${input.entityId}/items/${input.itemId}/references?${queryParams}`),
        {
          method: 'GET',
          headers
        }
      );

      if (!result.ok) {
        return this.error('Failed to get item references');
      }

      return await result.json();
    } catch {
      return this.error('Failed to get item references');
    }
  }
}
